import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import { ToastNotification } from "../../components/ToastNotification";
import { AUTH_STORED_ROUTE } from "../../constants/localStorageKeys";
import { useAuth } from "../../hooks/useAuth";
import {
  FooterLinksMobile,
  FooterLinksDesktop,
} from "../../layout/components/FooterLinks";
import { authService } from "../../services/authService";
import { preLoginLogo } from "../../styles/preLoginBranding";
import { extractErrors } from "../../utils/apiErrorUtils";
import { isSystemControllerUser } from "../../utils/isSystemControllerUser";
import { mixpanelActions } from "../../utils/mixpanel";
import { safeStorage } from "../../utils/storage";
import {
  Logo,
  Container,
  StyledTile,
  ContentContainer,
  LoadingOverlay,
  loginButtonStyles,
  CodeSubtitle,
  ResendCodeHyperlink,
  CodeDescription,
} from "./MFA.styles";

export function MFAPage() {
  const { t } = useTranslation();

  document.title = t("mfa_documentTitle");

  const navigate = useNavigate();
  const {
    authenticate,
    isAuthenticated,
    saveDataInLocalStorage,
    loginDetails,
    mfaDetails,
  } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [wasResendSuccessful, setWasResendSuccessful] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    if (isAuthenticated || mfaDetails === undefined) {
      navigate("/");
    }
  }, []);

  const isSubmittingRef = useRef(false);

  const handleOtpSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setIsLoading(true);

      const request = {
        email: mfaDetails !== undefined ? mfaDetails?.email : "",
        code: otp,
      };

      const confirmLoginMfaResponse = await authService.confirmLoginMfa(
        request
      );

      setIsLoading(false);

      if (confirmLoginMfaResponse.status === 400) {
        const errors = extractErrors(confirmLoginMfaResponse);
        setOtpError(errors[0]?.message ?? t("common_genericError"));
        return;
      }

      const {
        token,
        preferences,
        portalPreferences,
        intercomDetails,
        userDetails,
        hospitalConfiguration,
        staffPreferences,
        wardConfiguration,
        tenantDetails,
      } = confirmLoginMfaResponse.data;

      if (token !== undefined) {
        const storedRoute =
          safeStorage.getItem(AUTH_STORED_ROUTE) ?? "/patients";

        safeStorage.clear();

        await saveDataInLocalStorage({
          token,
          preferences,
          portalPreferences,
          intercomDetails,
          userDetails,
          hospitalConfiguration,
          staffPreferences,
          wardConfiguration,
          tenantDetails,
        });

        // EN-4961: SystemController users only have access to the reports
        // dashboard — send them straight there regardless of stored route. Role
        // identity rather than the admin-reports:read permission so future roles
        // that also receive the permission (e.g. HealthcareAdmin in Phase 2) keep
        // their default landing page.
        navigate(isSystemControllerUser() ? "/reports" : storedRoute);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleOTPChange = (e: any) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === "" || regex.test(e.target.value)) {
      setOtp(e.target.value);
    }
  };

  const handleResend = async () => {
    if (loginDetails === undefined) return;

    const { email, password } = loginDetails;

    const responseFromAPI = await authenticate({
      email,
      password,
    });

    if (responseFromAPI.errors === undefined) {
      setWasResendSuccessful(true);
      mixpanelActions.track("User Action: MFAResendCode");
    }

    setTimeout(() => {
      setWasResendSuccessful(false);
    }, 3500);
  };

  const handleButtonDisable = () => {
    if (otp.length !== 6) {
      return true;
    }

    return false;
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && !handleButtonDisable()) {
      handleOtpSubmit();
    }
  };

  return (
    <Container>
      <StyledTile>
        {isLoading && <LoadingOverlay />}

        <ContentContainer>
          <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />

          <div>
            <CodeDescription>
              <p>
                <Trans
                  i18nKey="mfa_codeSentTo"
                  values={{ contact: mfaDetails?.hiddenMfaContact }}
                  components={{ contact: <b /> }}
                />
              </p>
              <p>{t("mfa_enterCode")}</p>
            </CodeDescription>
            <TextField
              name="confirmationCode"
              label={t("mfa_codeLabel")}
              value={otp}
              onChange={handleOTPChange}
              onKeyDown={handleKeyDown}
              placeholder="00-00-00"
              errorMessage={otpError}
              backgroudColor="white"
              maxLength={6}
            />
            <div>
              <CodeSubtitle>
                <Trans
                  i18nKey="mfa_resendPrompt"
                  components={{
                    resend: <ResendCodeHyperlink onClick={handleResend} />,
                  }}
                />
              </CodeSubtitle>
              {wasResendSuccessful && (
                <ToastNotification
                  message={t("mfa_codeResent", {
                    contact: mfaDetails?.hiddenMfaContact,
                  })}
                  type="success"
                  width="450px"
                  rightMargin="45%"
                />
              )}
            </div>
          </div>
          <Button
            type="submit"
            label={t("mfa_submit")}
            isLoading={isLoading}
            className={loginButtonStyles()}
            disabled={handleButtonDisable()}
            onClick={handleOtpSubmit}
          />
        </ContentContainer>
        <FooterLinksMobile />
      </StyledTile>
      <FooterLinksDesktop />
    </Container>
  );
}
